import apiService, { authFetch } from '../../services/apiServices';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Switch, Stack, TextField, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, MenuItem, Typography, FormControlLabel, Grid, Divider, IconButton, Tooltip,
} from '@mui/material';
import {
  TemplateToolbar, TemplateGrid, TemplateState, TemplateCard, TemplateText, TemplateAction, templateActionSx,
} from './TemplateShell';
import Toast                from '../actions/Toasts';
import ConfirmDialog        from '../common/ConfirmDialog';
import WorkerPicker         from '../common/WorkerPicker';
import AddIcon              from '@mui/icons-material/Add';
import DeleteIcon           from '@mui/icons-material/DeleteOutline';
import GavelIcon            from '@mui/icons-material/Gavel';
import EditIcon             from '@mui/icons-material/Edit';
import SaveIcon             from '@mui/icons-material/Save';
import PictureAsPdfIcon     from '@mui/icons-material/PictureAsPdf';
import CategoryIcon         from '@mui/icons-material/CategoryOutlined';
import TuneIcon             from '@mui/icons-material/Tune';
import {
  DEFAULT_LAYOUT, fillDecisionPlaceholders, downloadDecisionPdf,
} from '../../utils/decisionPdf';

const JSON_HDRS = { 'Content-Type': 'application/json' };
const todayKey = () => new Date().toISOString().slice(0, 10);

// Një punëtor shembull — "Shiko shembull" duhet të tregojë PDF-në e vërtetë,
// jo një skicë, pa kërkuar që përdoruesi të zgjedhë dikë.
const SAMPLE_WORKER = { Id: 0, FirstName: 'Emri', LastName: 'Mbiemri', Position: 'Pozita', DepartmentName: 'Departamenti' };

const PLACEHOLDER_HINT = 'Vendmbajtës: [Emri Mbiemri], [Pozita], [Departamenti], [Kompania], [Vendi], [Data], [Data Përfundimit], [Data e Fillimit], [XX]';

async function api(path, opts) {
  const r = await authFetch(path, opts);
  return r.json().catch(() => ({ success: false }));
}

// ─── Shabllonet e Vendimeve ──────────────────────────────────────────────────
// Çdo shabllon i takon një lloji vendimi (decision_types). Llojet dhe struktura
// e PDF-së menaxhohen këtu; "Ndërpre Kontratën" përdor llojin `termination`,
// kurse "Gjenero" nxjerr PDF-në e çdo lloji për një punëtor të zgjedhur.
export default function DecisionTemplates() {
    const [templates, setTemplates] = useState([]);
    const [types, setTypes]         = useState([]);
    const [layout, setLayout]       = useState({});
    const [company, setCompany]     = useState({});
    const [workers, setWorkers]     = useState([]);
    const [loading, setLoading]     = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving]       = useState(false);
    const [editItem, setEditItem]   = useState(null);   // { id?, label, comment, type_id }
    const [confirmDel, setConfirmDel] = useState(null);
    const [toast, setToast]         = useState({ open: false, message: '', variant: 'success' });
    const [search, setSearch]       = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [typesOpen, setTypesOpen] = useState(false);
    const [layoutOpen, setLayoutOpen] = useState(false);
    const [layoutDraft, setLayoutDraft] = useState(DEFAULT_LAYOUT);
    const [gen, setGen]             = useState(null);   // { tpl, workerId, date, body }

    const say = (message, variant = 'success') => setToast({ open: true, message, variant });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [r, ty, lay, st, w] = await Promise.all([
                api('/api/termination-templates'),
                api('/api/decision-types'),
                api('/api/decision-settings'),
                apiService().get('settings').catch(() => null),
                apiService().get('workers').catch(() => null),
            ]);
            if (r?.success) { setTemplates(r.data || []); setLoadError(''); }
            else setLoadError(r?.error || 'Shabllonet nuk u ngarkuan.');
            if (ty?.success) setTypes(ty.data || []);
            if (lay?.success) setLayout(lay.data || {});
            setCompany(st?.data?.[0] || {});
            setWorkers(w?.data || []);
        } catch (_) {
            // Një server i rënë nuk duhet të duket si listë e zbrazët.
            setLoadError('Shabllonet nuk u ngarkuan. Kontrollo lidhjen me serverin.');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const typeById = useMemo(() => new Map(types.map((t) => [Number(t.id), t])), [types]);
    const terminationType = types.find((t) => t.code === 'termination');
    const activeTypes = types.filter((t) => t.active !== false);

    /* ── shabllonet ── */
    const saveTemplate = async () => {
        if (!editItem?.label?.trim()) return;
        setSaving(true);
        const payload = {
            label: editItem.label.trim(), comment: editItem.comment || '',
            sort_order: editItem.sort_order ?? templates.length, active: editItem.active ?? true,
            type_id: editItem.type_id || null,
        };
        try {
            const r = editItem.id
                ? await api(`/api/termination-templates/${editItem.id}`, { method: 'PUT', headers: JSON_HDRS, body: JSON.stringify(payload) })
                : await api('/api/termination-templates', { method: 'POST', headers: JSON_HDRS, body: JSON.stringify(payload) });
            if (r?.success) { setEditItem(null); load(); say(editItem.id ? 'Ndryshimet u ruajtën.' : 'Shablloni u shtua.'); }
            else say(r?.error || 'Veprimi nuk u krye.', 'error');
        } catch (_) { say('Veprimi nuk u krye.', 'error'); }
        setSaving(false);
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            const r = await api(`/api/termination-templates/${confirmDel.id}`, { method: 'DELETE' });
            if (r?.success) { setConfirmDel(null); load(); say('Shablloni u fshi.'); }
            else say(r?.error || 'Fshirja nuk u krye.', 'error');
        } catch (_) { say('Fshirja nuk u krye.', 'error'); }
        setSaving(false);
    };

    const toggleActive = async (tpl) => {
        try {
            await api(`/api/termination-templates/${tpl.id}`, {
                method: 'PUT', headers: JSON_HDRS,
                body: JSON.stringify({ label: tpl.label, comment: tpl.comment || '', sort_order: tpl.sort_order, active: !tpl.active }),
            });
            load();
        } catch (_) {}
    };

    /* ── llojet ── */
    const saveType = async (t) => {
        if (!t.name?.trim()) { say('Emri i llojit mungon.', 'error'); return false; }
        const body = JSON.stringify({ name: t.name, title: t.title, legal_basis: t.legal_basis, sort_order: t.sort_order ?? types.length, active: t.active !== false });
        const r = t.id
            ? await api(`/api/decision-types/${t.id}`, { method: 'PUT', headers: JSON_HDRS, body })
            : await api('/api/decision-types', { method: 'POST', headers: JSON_HDRS, body });
        if (!r?.success) { say(r?.error || 'Lloji nuk u ruajt.', 'error'); return false; }
        say('Lloji u ruajt.');
        load();
        return true;
    };
    const deleteType = async (t) => {
        const r = await api(`/api/decision-types/${t.id}`, { method: 'DELETE' });
        if (r?.success) { say('Lloji u fshi.'); load(); } else say(r?.error || 'Lloji nuk u fshi.', 'error');
    };

    /* ── struktura e PDF-së ── */
    const openLayout = () => { setLayoutDraft({ ...DEFAULT_LAYOUT, ...layout }); setLayoutOpen(true); };
    const saveLayout = async () => {
        setSaving(true);
        const r = await api('/api/decision-settings', { method: 'PUT', headers: JSON_HDRS, body: JSON.stringify(layoutDraft) });
        setSaving(false);
        if (r?.success) { setLayout(r.data || {}); setLayoutOpen(false); say('Struktura e PDF-së u ruajt.'); }
        else say(r?.error || 'Struktura nuk u ruajt.', 'error');
    };
    const previewLayout = () => {
        const type = terminationType || activeTypes[0] || { name: 'Vendim', title: 'VENDIM' };
        const tpl = templates.find((t) => Number(t.type_id) === Number(type.id)) || templates[0];
        downloadDecisionPdf({
            type, layout: layoutDraft, company, worker: SAMPLE_WORKER, date: todayKey(),
            body: fillDecisionPlaceholders(tpl?.comment || 'Teksti i vendimit shfaqet këtu.', { worker: SAMPLE_WORKER, date: todayKey(), layout: layoutDraft, company }),
        }, 'vendim-shembull.pdf');
    };

    /* ── gjenerimi ── */
    const genWorker = gen ? workers.find((w) => String(w.Id ?? w.id) === String(gen.workerId)) : null;
    const startGenerate = (tpl) => setGen({ tpl, workerId: '', date: todayKey(), body: tpl.comment || '' });
    const generate = () => {
        if (!gen || !genWorker) return;
        const type = typeById.get(Number(gen.tpl.type_id)) || terminationType || { name: gen.tpl.label, title: gen.tpl.label };
        const ctx = { worker: genWorker, date: gen.date, layout, company };
        const name = `${genWorker.FirstName || ''}-${genWorker.LastName || ''}`.replace(/\s+/g, '-');
        downloadDecisionPdf({ type, layout, company, worker: genWorker, date: gen.date, body: fillDecisionPlaceholders(gen.body, ctx) },
            `vendim-${(type.code || 'vendim')}-${name}.pdf`);
    };

    const filtered = templates.filter((t) => {
        if (typeFilter !== 'all' && String(t.type_id) !== String(typeFilter)) return false;
        const q = search.trim().toLowerCase();
        return !q || t.label.toLowerCase().includes(q) || (t.comment || '').toLowerCase().includes(q);
    });

    return (
        <Box>
            <TemplateToolbar
                icon={<GavelIcon />}
                title="Shabllonet e Vendimeve"
                hint={'Çdo shabllon i takon një lloji vendimi. Llojet "Ndërprerje e kontratës" shfaqen te "Ndërpre Kontratën"; çdo shabllon mund të gjenerohet si PDF për një punëtor.'}
                search={search}
                onSearch={setSearch}
                action={
                    <Stack direction="row" spacing={0.75}>
                        <Button variant="outlined" size="small" startIcon={<CategoryIcon sx={{ fontSize: 14 }} />}
                            onClick={() => setTypesOpen(true)} sx={templateActionSx}>
                            Llojet
                        </Button>
                        <Button variant="outlined" size="small" startIcon={<TuneIcon sx={{ fontSize: 14 }} />}
                            onClick={openLayout} sx={templateActionSx}>
                            Struktura e PDF-së
                        </Button>
                        <Button variant="contained" size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                            onClick={() => setEditItem({ label: '', comment: '', type_id: typeFilter !== 'all' ? Number(typeFilter) : terminationType?.id || '' })}
                            sx={templateActionSx}>
                            Shto shabllon
                        </Button>
                    </Stack>
                }
            />

            {/* Filtri sipas llojit */}
            {types.length > 0 && (
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                    <Chip size="small" label={`Të gjitha (${templates.length})`} color={typeFilter === 'all' ? 'primary' : 'default'}
                        variant={typeFilter === 'all' ? 'filled' : 'outlined'} onClick={() => setTypeFilter('all')} />
                    {activeTypes.map((t) => {
                        const n = templates.filter((x) => Number(x.type_id) === Number(t.id)).length;
                        const on = String(typeFilter) === String(t.id);
                        return (
                            <Chip key={t.id} size="small" label={`${t.name} (${n})`} color={on ? 'primary' : 'default'}
                                variant={on ? 'filled' : 'outlined'} onClick={() => setTypeFilter(String(t.id))} />
                        );
                    })}
                </Stack>
            )}

            <TemplateState
                loading={loading}
                error={loadError}
                empty={filtered.length === 0
                    ? (search.trim() ? `Asnjë rezultat për "${search}"` : 'Asnjë shabllon për këtë lloj. Shto të parin me "Shto shabllon".')
                    : ''}
            >
                <TemplateGrid>
                    {filtered.map((tpl) => (
                        <TemplateCard
                            key={tpl.id}
                            dim={tpl.active === false}
                            title={tpl.label}
                            aside={<Switch size="small" checked={tpl.active !== false} onChange={() => toggleActive(tpl)} />}
                            actions={
                                <>
                                    <TemplateAction label="Gjenero" icon={<PictureAsPdfIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => startGenerate(tpl)} />
                                    <TemplateAction label="Ndrysho" icon={<EditIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => setEditItem({ ...tpl })} />
                                    <TemplateAction label="Fshi" danger icon={<DeleteIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => setConfirmDel(tpl)} />
                                </>
                            }
                        >
                            <Chip size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', mb: 0.75 }}
                                label={typeById.get(Number(tpl.type_id))?.name || 'Pa lloj'} />
                            <TemplateText muted={!tpl.comment}>{tpl.comment || 'Pa tekst'}</TemplateText>
                        </TemplateCard>
                    ))}
                </TemplateGrid>
            </TemplateState>

            {/* Dialog: Shto / Ndrysho shabllonin */}
            <Dialog open={Boolean(editItem)} onClose={() => !saving && setEditItem(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{editItem?.id ? 'Ndrysho shabllonin' : 'Shto shabllon të ri'}</DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <Stack spacing={2}>
                        <TextField select label="Lloji i vendimit" size="small" fullWidth
                            value={editItem?.type_id || ''}
                            onChange={(e) => setEditItem((p) => ({ ...p, type_id: Number(e.target.value) }))}>
                            {activeTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                        </TextField>
                        <TextField label="Emri i shabllonit" value={editItem?.label || ''} autoFocus
                            onChange={(e) => setEditItem((p) => ({ ...p, label: e.target.value }))}
                            fullWidth size="small" placeholder="p.sh. Skadim kontrate" />
                        <TextField label="Teksti i vendimit" value={editItem?.comment || ''}
                            onChange={(e) => setEditItem((p) => ({ ...p, comment: e.target.value }))}
                            fullWidth size="small" multiline minRows={5}
                            helperText={`${PLACEHOLDER_HINT}. Rreshti i ri = paragraf i ri në PDF.`} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditItem(null)} disabled={saving}>Anulo</Button>
                    <Button onClick={saveTemplate} variant="contained"
                        disabled={saving || !editItem?.label?.trim()}
                        startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon />}>
                        {saving ? 'Duke ruajtur…' : 'Ruaj'}
                    </Button>
                </DialogActions>
            </Dialog>

            <TypesDialog open={typesOpen} onClose={() => setTypesOpen(false)} types={types}
                onSave={saveType} onDelete={deleteType} />

            {/* Dialog: Struktura e PDF-së */}
            <Dialog open={layoutOpen} onClose={() => !saving && setLayoutOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Struktura e PDF-së së vendimit</DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        Fushat bosh merren nga profili i kompanisë (emri, adresa, qyteti, drejtori, logoja).
                        Titulli dhe baza ligjore vijnë nga lloji i vendimit.
                    </Typography>
                    <LayoutSection title="Koka">
                        <Grid item xs={12} sm={6}><LayoutField label="Titulli i kokës" k="headerTitle" placeholder={company.CompanyName} draft={layoutDraft} set={setLayoutDraft} /></Grid>
                        <Grid item xs={12} sm={6}><LayoutField label="Nëntitulli" k="headerSubtitle" placeholder={company.Address} draft={layoutDraft} set={setLayoutDraft} /></Grid>
                        <Grid item xs={6} sm={3}><LayoutField label="Prefiksi i numrit" k="numberPrefix" draft={layoutDraft} set={setLayoutDraft} /></Grid>
                        <Grid item xs={6} sm={3}><LayoutField label="Qyteti" k="city" placeholder={company.City || 'Prishtinë'} draft={layoutDraft} set={setLayoutDraft} /></Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField label="Ngjyra" type="color" size="small" fullWidth value={layoutDraft.accentColor || '#1E4E5A'}
                                onChange={(e) => setLayoutDraft((p) => ({ ...p, accentColor: e.target.value }))} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <FormControlLabel label="Logoja e kompanisë" control={
                                <Switch checked={layoutDraft.showLogo !== false} onChange={(e) => setLayoutDraft((p) => ({ ...p, showLogo: e.target.checked }))} />} />
                        </Grid>
                    </LayoutSection>
                    <LayoutSection title="Përmbajtja">
                        <Grid item xs={6} sm={3}><LayoutField label="Neni [XX]" k="legalArticle" draft={layoutDraft} set={setLayoutDraft} /></Grid>
                        <Grid item xs={6} sm={9}>
                            <FormControlLabel label="Tabela me të dhënat e punëmarrësit" control={
                                <Switch checked={layoutDraft.showInfoTable !== false} onChange={(e) => setLayoutDraft((p) => ({ ...p, showInfoTable: e.target.checked }))} />} />
                        </Grid>
                        <Grid item xs={12}><LayoutField label="Teksti përmbyllës" k="closingText" multiline draft={layoutDraft} set={setLayoutDraft} /></Grid>
                    </LayoutSection>
                    <LayoutSection title="Nënshkrimi dhe footer-i">
                        <Grid item xs={12} sm={4}><LayoutField label="Titulli i nënshkruesit" k="signatoryTitle" draft={layoutDraft} set={setLayoutDraft} /></Grid>
                        <Grid item xs={12} sm={4}><LayoutField label="Emri i nënshkruesit" k="signatoryName" placeholder={company.Director} draft={layoutDraft} set={setLayoutDraft} /></Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControlLabel label="Nënshkrimi i punëmarrësit" control={
                                <Switch checked={layoutDraft.showEmployeeSignature !== false} onChange={(e) => setLayoutDraft((p) => ({ ...p, showEmployeeSignature: e.target.checked }))} />} />
                        </Grid>
                        <Grid item xs={12}><LayoutField label="Teksti i footer-it" k="footerText" placeholder={[company.CompanyName, company.Address].filter(Boolean).join(' · ')} draft={layoutDraft} set={setLayoutDraft} /></Grid>
                    </LayoutSection>
                </DialogContent>
                <DialogActions>
                    <Button onClick={previewLayout} startIcon={<PictureAsPdfIcon />} sx={{ mr: 'auto' }}>Shiko shembull</Button>
                    <Button onClick={() => setLayoutOpen(false)} disabled={saving}>Anulo</Button>
                    <Button onClick={saveLayout} variant="contained" disabled={saving}
                        startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon />}>Ruaj</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Gjenero vendimin */}
            <Dialog open={Boolean(gen)} onClose={() => setGen(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Gjenero vendimin — {gen?.tpl?.label}</DialogTitle>
                <DialogContent sx={{ pt: '12px !important' }}>
                    <Stack spacing={2}>
                        <WorkerPicker workers={workers} value={gen?.workerId || ''} required
                            onChange={(v) => setGen((p) => ({ ...p, workerId: v, body: p.tpl.comment || '' }))} />
                        <TextField label="Data e hyrjes në fuqi" type="date" size="small" InputLabelProps={{ shrink: true }}
                            value={gen?.date || ''} onChange={(e) => setGen((p) => ({ ...p, date: e.target.value }))} />
                        <TextField label="Teksti" multiline minRows={6} size="small" fullWidth
                            value={genWorker ? fillDecisionPlaceholders(gen?.body, { worker: genWorker, date: gen?.date, layout, company }) : (gen?.body || '')}
                            onChange={(e) => setGen((p) => ({ ...p, body: e.target.value }))}
                            helperText="Ndryshimet këtu vlejnë vetëm për këtë vendim, jo për shabllonin." />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGen(null)}>Mbyll</Button>
                    <Button variant="contained" startIcon={<PictureAsPdfIcon />} disabled={!genWorker} onClick={generate}>Shkarko PDF</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirmDel)}
                title="Fshi shabllonin"
                message="Vendimet e lëshuara më parë me këtë shabllon nuk preken — teksti i tyre është ruajtur bashkë me vendimin."
                details={confirmDel ? confirmDel.label : ''}
                confirmLabel="Fshi"
                busy={saving}
                onConfirm={handleDelete}
                onClose={() => setConfirmDel(null)}
            />

            <Toast open={toast.open} message={toast.message} variant={toast.variant}
                onClose={() => setToast(t => ({ ...t, open: false }))} />
        </Box>
    );
}

function LayoutSection({ title, children }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', mb: 1 }}>{title}</Typography>
            <Grid container spacing={1.5}>{children}</Grid>
            <Divider sx={{ mt: 2 }} />
        </Box>
    );
}

function LayoutField({ label, k, draft, set, placeholder, multiline = false }) {
    return (
        <TextField label={label} size="small" fullWidth multiline={multiline} minRows={multiline ? 2 : undefined}
            value={draft[k] ?? ''} placeholder={placeholder || ''} InputLabelProps={{ shrink: true }}
            onChange={(e) => set((p) => ({ ...p, [k]: e.target.value }))} />
    );
}

// Menaxhimi i llojeve — një listë e redaktueshme në vend, pa dritare të dyta.
function TypesDialog({ open, onClose, types, onSave, onDelete }) {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (open) setRows(types.map((t) => ({ ...t }))); }, [open, types]);
    const update = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    const save = async (r) => { setBusy(true); await onSave(r); setBusy(false); };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Llojet e vendimeve</DialogTitle>
            <DialogContent sx={{ pt: '12px !important' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                    Titulli shfaqet si titull i madh në PDF; baza ligjore si paragrafi hyrës ("Në mbështetje të…").
                </Typography>
                <Stack spacing={1.5} divider={<Divider />}>
                    {rows.map((r, i) => (
                        <Grid container spacing={1} key={r.id || `new-${i}`} alignItems="flex-start">
                            <Grid item xs={12} sm={3}>
                                <TextField label="Emri" size="small" fullWidth value={r.name || ''} onChange={(e) => update(i, { name: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Titulli në PDF" size="small" fullWidth value={r.title || ''} onChange={(e) => update(i, { title: e.target.value })} />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField label="Baza ligjore" size="small" fullWidth multiline maxRows={3} value={r.legal_basis || ''} onChange={(e) => update(i, { legal_basis: e.target.value })} />
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <FormControlLabel label="Aktiv" control={<Switch size="small" checked={r.active !== false} onChange={(e) => update(i, { active: e.target.checked })} />} />
                                    {r.code === 'termination' && <Chip size="small" label="Përdoret nga Ndërpre Kontratën" variant="outlined" />}
                                    <Box sx={{ flex: 1 }} />
                                    <Button size="small" variant="outlined" disabled={busy} startIcon={<SaveIcon sx={{ fontSize: 14 }} />} onClick={() => save(r)}>Ruaj</Button>
                                    {r.id && r.code !== 'termination' && (
                                        <Tooltip title="Fshi llojin (shabllonet mbeten pa lloj)">
                                            <IconButton size="small" color="error" onClick={() => onDelete(r)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    ))}
                </Stack>
                <Button size="small" startIcon={<AddIcon />} sx={{ mt: 2 }}
                    onClick={() => setRows((rs) => [...rs, { name: '', title: '', legal_basis: '', active: true, sort_order: rs.length }])}>
                    Shto lloj
                </Button>
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Mbyll</Button></DialogActions>
        </Dialog>
    );
}
